import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/format';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';

interface LefeuvreTransaction {
  id: string;
  property_number: string;
  seller: string;
  buyer: string;
  copro_id?: string;
  property_origin?: string;
  lefeuvre_gestion?: boolean;
  lefeuvre_syndic?: boolean;
  lead_origin?: string;
  compromise_date?: string;
  acte_date?: string;
  fees_with_tax: number;
  fees_without_tax: number;
  net_fees_without_tax: number;
  status: string;
  copro?: {
    residence: string;
  };
}

export default function ReportingLefeuvre() {
  const [transactions, setTransactions] = useState<LefeuvreTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLefeuvreTransactions();
  }, []);

  const fetchLefeuvreTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch transactions with Lefeuvre origin and join with copros table
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          copro:copros (
            residence
          )
        `)
        .or('lefeuvre_gestion.eq.true,lefeuvre_syndic.eq.true')
        .in('status', ['compromis', 'acte', 'encaisse', 'cloture'])
        .order('compromise_date', { ascending: false });

      if (error) throw error;

      console.log('Lefeuvre transactions loaded:', data);
      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching Lefeuvre transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateRetrocession = (feesWithoutTax: number) => {
    return feesWithoutTax * 0.20;
  };

  const calculateNetFees = (feesWithoutTax: number) => {
    return feesWithoutTax * 0.80;
  };

  const getOriginGestion = (transaction: LefeuvreTransaction) => {
    return transaction.lefeuvre_gestion ? 'OUI' : 'NON';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' });
  };

  const getResidence = (transaction: LefeuvreTransaction) => {
    if (transaction.copro?.residence) {
      return transaction.copro.residence;
    }
    return transaction.property_number || '-';
  };

  const handleExportExcel = () => {
    const exportData = transactions.map(transaction => ({
      'Nom & Prénom Client Propriétaire Vendeur': transaction.seller,
      'Résidence': getResidence(transaction),
      'Origine Gestion': getOriginGestion(transaction),
      'Date signature compromis': formatDate(transaction.compromise_date),
      'Date Acte': formatDate(transaction.acte_date),
      'Honoraires TTC avant rétroc.': transaction.fees_with_tax,
      'Honoraires HT': transaction.fees_without_tax,
      'Honoraires Agence HT Nette perçue': calculateNetFees(transaction.fees_without_tax),
      'Rétrocessions HT (20%)': calculateRetrocession(transaction.fees_without_tax)
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporting Lefeuvre');
    
    const fileName = `reporting_lefeuvre_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={handleExportExcel}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Exporter Excel
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom & Prénom<br />Client Propriétaire<br />Vendeur
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Résidence
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Origine<br />Gestion
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date<br />signature<br />compromis
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date Acte
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Honoraires<br />TTC<br /><br />avant rétroc.
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Honoraires<br />HT
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Honoraires<br />Agence HT<br />Nette perçue
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rétrocession<br />s HT (20%)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {transaction.seller}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {getResidence(transaction)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-900">
                    {getOriginGestion(transaction)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {formatDate(transaction.compromise_date)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {formatDate(transaction.acte_date)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(transaction.fees_with_tax)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(transaction.fees_without_tax)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(calculateNetFees(transaction.fees_without_tax))}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(calculateRetrocession(transaction.fees_without_tax))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {transactions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune transaction Lefeuvre trouvée</p>
          </div>
        )}
      </div>
    </div>
  );
}